import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { TECHNOLOGIES } from '../utils/technologies';

const STATUS_COLOURS = {
  draft: '#aaa', generating: '#E97132', generated: '#e6a817', complete: '#196B24', error: '#c0392b',
};

export default function ReportDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [uploadingSite, setUploadingSite] = useState(null);
  const [editingSite, setEditingSite] = useState(null);
  const [newSiteName, setNewSiteName] = useState('');
  const [addingSite, setAddingSite] = useState(null); // section_id being added to
  const [newSiteInput, setNewSiteInput] = useState('');
  const fileInputRef = useRef();
  const [activeUploadSite, setActiveUploadSite] = useState(null);
  const [sites, setSites] = useState({}); // {section_id: [sites]}
  const [scripts, setScripts] = useState({}); // {technology: [scripts]}

  async function fetchReport() {
    try {
      const { data } = await api.get(`/reports/${id}`);
      setReport(data);
      // Fetch sites for each section
      const sitesData = {};
      for (const section of (data.technologies || [])) {
        const { data: siteList } = await api.get(`/sites/section/${section.id}`);
        sitesData[section.id] = siteList;
      }
      setSites(sitesData);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { fetchReport(); }, [id]);

  const techKey = report ? (report.technologies || []).map(s => s.technology).join(',') : '';
  useEffect(() => {
    if (!techKey) return;
    (async () => {
      const result = {};
      for (const tech of Array.from(new Set(techKey.split(',')))) {
        try {
          const { data } = await api.get(`/scripts/${tech}`);
          result[tech] = data;
        } catch (e) {
          result[tech] = [];
        }
      }
      setScripts(result);
    })();
  }, [techKey]);

  function getTechLabel(tech) {
    const found = TECHNOLOGIES.find(t => t.technology === tech);
    return found?.label || tech;
  }

  function getTechFileHints(tech) {
    const found = TECHNOLOGIES.find(t => t.technology === tech);
    return found?.fileHints || [];
  }

  function triggerUpload(siteId) {
    setActiveUploadSite(siteId);
    fileInputRef.current.click();
  }

  async function handleFileChange(e) {
    const files = Array.from(e.target.files);
    if (!files.length || !activeUploadSite) return;
    setUploadingSite(activeUploadSite);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        await api.post(`/uploads/site/${activeUploadSite}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      await fetchReport();
    } catch (err) {
      alert('Upload failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setUploadingSite(null);
      e.target.value = '';
    }
  }

  async function addSite(sectionId) {
    if (!newSiteInput.trim()) return;
    await api.post(`/sites/section/${sectionId}`, { name: newSiteInput.trim(), order_index: (sites[sectionId] || []).length });
    setNewSiteInput('');
    setAddingSite(null);
    await fetchReport();
  }

  async function renameSite(siteId, name) {
    await api.patch(`/sites/${siteId}`, { name });
    setEditingSite(null);
    await fetchReport();
  }

  async function deleteSite(siteId) {
    if (!window.confirm('Delete this site and all its files?')) return;
    await api.delete(`/sites/${siteId}`);
    await fetchReport();
  }

  async function deleteFile(fileId) {
    if (!window.confirm('Remove this file?')) return;
    try {
      await api.delete(`/uploads/file/${fileId}`);
      await fetchReport();
    } catch (err) {
      alert('Error deleting file: ' + (err.response?.data?.detail || err.message));
    }
  }

  async function downloadScript(tech, filename) {
    try {
      const resp = await api.get(`/scripts/${tech}/${encodeURIComponent(filename)}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Erro ao descarregar o script: ' + (err.response?.data?.detail || err.message));
    }
  }

  async function handleGenerate() {
    setGenerating(true);
    try {
      await api.post(`/reports/${id}/generate`);
      await fetchReport();
    } catch (err) {
      alert('Generation failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setGenerating(false);
    }
  }

  async function handleDownload() {
    const resp = await api.get(`/reports/${id}/download`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([resp.data]));
    const a = document.createElement('a');
    a.href = url;
    a.download = `DOCK_${report.project_title.replace(/\s+/g, '_')}.docx`;
    a.click();
    window.URL.revokeObjectURL(url);
  }

  if (loading) return <div style={styles.loading}>Loading…</div>;
  if (!report) return <div style={styles.loading}>Report not found.</div>;

  const sections = report.technologies || [];
  const isComplete = report.status === 'generated';
  const canGenerate = sections.length > 0 && report.status !== 'generating';

  return (
    <div style={styles.page}>
      <header style={styles.header}>
        <button style={styles.backBtn} onClick={() => navigate('/')}>← Back</button>
        <span style={styles.headerTitle}>{report.project_title}</span>
        <span style={{ ...styles.statusBadge, background: STATUS_COLOURS[report.status] || '#aaa' }}>
          {report.status}
        </span>
      </header>

      <main style={styles.main}>
        <div style={styles.metaCard}>
          <div style={styles.metaGrid}>
            {report.client_name && <div style={styles.metaItem}><span style={styles.metaKey}>Client</span>{report.client_name}</div>}
            {report.me_ticket_ref && <div style={styles.metaItem}><span style={styles.metaKey}>ME Ticket</span>#{report.me_ticket_ref}</div>}
            <div style={styles.metaItem}><span style={styles.metaKey}>Created</span>{report.created_at?.slice(0, 10)}</div>
            <div style={styles.metaItem}><span style={styles.metaKey}>Technician</span>{report.technician_name}</div>
          </div>
        </div>

        <h2 style={styles.sectionTitle}>Technologies</h2>
        <div style={styles.sectionList}>
          {sections.map(section => {
            const sectionSites = sites[section.id] || [];
            const hasDefaultOnly = sectionSites.length === 1 && sectionSites[0].name === 'Default';
            const hints = getTechFileHints(section.technology);

            return (
              <div key={section.id} style={styles.sectionCard}>
                {/* Technology header */}
                <div style={styles.sectionHeader}>
                  <div>
                    <div style={styles.sectionName}>{getTechLabel(section.technology)}</div>
                    <div style={styles.sectionCat}>{section.category}</div>
                  </div>
                  <button style={styles.addSiteBtn} onClick={() => setAddingSite(section.id)}>
                    + Add Site
                  </button>
                </div>

                {/* File hints */}
                {hints.length > 0 && (
                  <div style={styles.uploadHints}>
                    <span style={styles.uploadHintsLabel}>Expected files:</span>
                    {hints.map((h, i) => <span key={i} style={styles.uploadHintItem}>• {h}</span>)}
                  </div>
                )}

                {/* Collector scripts */}
                {(scripts[section.technology] || []).length > 0 && (
                  <div style={styles.scriptBox}>
                    <span style={styles.uploadHintsLabel}>Scripts de recolha:</span>
                    {scripts[section.technology].map(s => (
                      <div key={s.file} style={styles.scriptRow}>
                        <div style={{ flex: 1 }}>
                          <div style={styles.scriptTitle}>{s.title}</div>
                          {s.description && <div style={styles.scriptDesc}>{s.description}</div>}
                        </div>
                        <button style={styles.scriptBtn} onClick={() => downloadScript(section.technology, s.file)}>
                          ⬇ {s.file}
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add site input */}
                {addingSite === section.id && (
                  <div style={styles.addSiteRow}>
                    <input
                      style={styles.siteInput}
                      value={newSiteInput}
                      onChange={e => setNewSiteInput(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && addSite(section.id)}
                      placeholder="Site name (e.g. Lisboa — Principal)"
                      autoFocus
                    />
                    <button style={styles.siteConfirmBtn} onClick={() => addSite(section.id)}>Add</button>
                    <button style={styles.siteCancelBtn} onClick={() => { setAddingSite(null); setNewSiteInput(''); }}>Cancel</button>
                  </div>
                )}

                {/* Sites */}
                {sectionSites.map(site => (
                  <div key={site.id} style={styles.siteBlock}>
                    {/* Site header */}
                    <div style={styles.siteHeader}>
                      {editingSite === site.id ? (
                        <div style={styles.addSiteRow}>
                          <input
                            style={styles.siteInput}
                            value={newSiteName}
                            onChange={e => setNewSiteName(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && renameSite(site.id, newSiteName)}
                            autoFocus
                          />
                          <button style={styles.siteConfirmBtn} onClick={() => renameSite(site.id, newSiteName)}>Save</button>
                          <button style={styles.siteCancelBtn} onClick={() => setEditingSite(null)}>Cancel</button>
                        </div>
                      ) : (
                        <div style={styles.siteNameRow}>
                          <span style={styles.siteName}>
                            {hasDefaultOnly ? '📁 Files' : `📍 ${site.name}`}
                          </span>
                          <div style={styles.siteActions}>
                            {!hasDefaultOnly && (
                              <button style={styles.siteActionBtn} onClick={() => { setEditingSite(site.id); setNewSiteName(site.name); }}>✎</button>
                            )}
                            <button style={styles.uploadBtn} onClick={() => triggerUpload(site.id)} disabled={uploadingSite === site.id}>
                              {uploadingSite === site.id ? 'Uploading…' : '+ Upload'}
                            </button>
                            {!hasDefaultOnly && (
                              <button style={styles.deleteSiteBtn} onClick={() => deleteSite(site.id)}>✕</button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Files */}
                    {site.files && site.files.length > 0 ? (
                      <div style={styles.fileList}>
                        {site.files.map(f => (
                          <div key={f.id} style={styles.fileRow}>
                            <span style={styles.fileIcon}>📄</span>
                            <span style={styles.fileName}>{f.original_filename}</span>
                            <span style={styles.fileSize}>{((f.file_size || 0) / 1024).toFixed(1)} KB</span>
                            <span style={{
                              ...styles.parsedBadge,
                              background: f.parsed === 'done' ? '#e8f5e9' : f.parsed === 'failed' ? '#fdecea' : '#fff3e0',
                              color: f.parsed === 'done' ? '#2e7d32' : f.parsed === 'failed' ? '#c0392b' : '#e65100'
                            }}>
                              {f.parsed === 'done' ? '✓ parsed' : f.parsed === 'failed' ? '✗ failed' : f.parsed === 'skipped' ? '○ skipped' : f.parsed}
                            </span>
                            <button style={styles.deleteFileBtn} onClick={() => deleteFile(f.id)}>✕</button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p style={styles.noFiles}>No files uploaded yet.</p>
                    )}
                  </div>
                ))}

                {sectionSites.length === 0 && (
                  <div style={styles.siteBlock}>
                    <p style={styles.noFiles}>No sites yet. Click "+ Add Site" or upload files directly.</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div style={styles.actionBar}>
          <button style={styles.generateBtn} onClick={handleGenerate} disabled={!canGenerate || generating}>
            {generating ? '⏳ Generating…' : isComplete ? '↻ Regenerate' : '🚀 Generate Report'}
          </button>
          {isComplete && (
            <button style={styles.downloadBtn} onClick={handleDownload}>
              ⬇ Download .docx
            </button>
          )}
        </div>
      </main>

      <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }}
        onChange={handleFileChange}
        accept=".xlsx,.xls,.csv,.txt,.xml,.log,.logs,.gz,.tgz,.zip,.pdf,.conf,.cfg,.info" />
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: '#f4f6f9', fontFamily: 'system-ui, sans-serif' },
  header: { background: '#0E2841', color: '#fff', padding: '0 32px', height: 60, display: 'flex', alignItems: 'center', gap: 16 },
  backBtn: { background: 'transparent', border: 'none', color: '#cdd9e5', fontSize: 14, cursor: 'pointer' },
  headerTitle: { fontSize: 16, fontWeight: 600, flex: 1 },
  statusBadge: { fontSize: 11, color: '#fff', padding: '3px 10px', borderRadius: 10, fontWeight: 700, textTransform: 'uppercase' },
  main: { maxWidth: 900, margin: '0 auto', padding: '32px 24px' },
  metaCard: { background: '#fff', borderRadius: 10, padding: '16px 20px', marginBottom: 28, border: '1px solid #E8E8E8' },
  metaGrid: { display: 'flex', gap: 32, flexWrap: 'wrap' },
  metaItem: { fontSize: 14, color: '#555' },
  metaKey: { display: 'block', fontSize: 11, fontWeight: 700, color: '#aaa', textTransform: 'uppercase', marginBottom: 2 },
  sectionTitle: { fontSize: 16, fontWeight: 700, color: '#0E2841', marginBottom: 12 },
  sectionList: { display: 'flex', flexDirection: 'column', gap: 16 },
  sectionCard: { background: '#fff', borderRadius: 10, padding: 20, border: '1px solid #E8E8E8' },
  sectionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 },
  sectionName: { fontSize: 16, fontWeight: 600, color: '#0E2841' },
  sectionCat: { fontSize: 12, color: '#156082', textTransform: 'uppercase', marginTop: 2 },
  addSiteBtn: { background: '#f4f6f9', border: '1.5px solid #E8E8E8', color: '#0E2841', padding: '6px 12px', borderRadius: 8, fontSize: 12, cursor: 'pointer', fontWeight: 600 },
  addSiteRow: { display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center' },
  siteInput: { flex: 1, padding: '8px 12px', border: '1.5px solid #156082', borderRadius: 8, fontSize: 14, outline: 'none' },
  siteConfirmBtn: { background: '#156082', color: '#fff', border: 'none', padding: '8px 14px', borderRadius: 8, fontSize: 13, cursor: 'pointer', fontWeight: 600 },
  siteCancelBtn: { background: 'transparent', color: '#aaa', border: '1px solid #E8E8E8', padding: '8px 12px', borderRadius: 8, fontSize: 13, cursor: 'pointer' },
  siteBlock: { background: '#f9f9f9', borderRadius: 8, padding: '12px 14px', marginBottom: 10, border: '1px solid #f0f0f0' },
  siteHeader: { marginBottom: 10 },
  siteNameRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  siteName: { fontSize: 14, fontWeight: 600, color: '#0E2841' },
  siteActions: { display: 'flex', gap: 8, alignItems: 'center' },
  siteActionBtn: { background: 'transparent', border: 'none', color: '#aaa', cursor: 'pointer', fontSize: 14 },
  deleteSiteBtn: { background: 'transparent', border: 'none', color: '#bbb', cursor: 'pointer', fontSize: 14 },
  uploadBtn: { background: '#f4f6f9', border: '1.5px solid #E8E8E8', color: '#0E2841', padding: '5px 12px', borderRadius: 8, fontSize: 12, cursor: 'pointer', fontWeight: 500 },
  uploadHints: { marginBottom: 12, padding: '8px 12px', background: '#f0f7ff', borderRadius: 6, display: 'flex', flexDirection: 'column', gap: 3 },
  uploadHintsLabel: { fontSize: 11, fontWeight: 700, color: '#156082', marginBottom: 2 },
  uploadHintItem: { fontSize: 11, color: '#555' },
  scriptBox: { marginBottom: 12, padding: '8px 12px', background: '#f1f8f1', borderRadius: 6, display: 'flex', flexDirection: 'column', gap: 6 },
  scriptRow: { display: 'flex', alignItems: 'center', gap: 12 },
  scriptTitle: { fontSize: 12, fontWeight: 600, color: '#0E2841' },
  scriptDesc: { fontSize: 11, color: '#555' },
  scriptBtn: { background: '#fff', border: '1.5px solid #196B24', color: '#196B24', padding: '5px 12px', borderRadius: 8, fontSize: 12, cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap' },
  fileList: { display: 'flex', flexDirection: 'column', gap: 6 },
  fileRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '7px 10px', background: '#fff', borderRadius: 6, fontSize: 13 },
  fileIcon: { fontSize: 14 },
  fileName: { flex: 1, color: '#333' },
  fileSize: { color: '#aaa', fontSize: 12 },
  parsedBadge: { fontSize: 11, padding: '2px 7px', borderRadius: 8, fontWeight: 600 },
  noFiles: { fontSize: 13, color: '#bbb', fontStyle: 'italic', margin: 0 },
  deleteFileBtn: { background: 'transparent', border: 'none', color: '#bbb', cursor: 'pointer', fontSize: 14, padding: '0 4px' },
  actionBar: { marginTop: 32, display: 'flex', gap: 12 },
  generateBtn: { background: '#156082', color: '#fff', border: 'none', padding: '13px 28px', borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: 'pointer' },
  downloadBtn: { background: '#196B24', color: '#fff', border: 'none', padding: '13px 28px', borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: 'pointer' },
  loading: { padding: 40, textAlign: 'center', color: '#aaa' },
};
